import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getStorage, ref, getDownloadURL } from "firebase/storage";

const ReferenceWindow = ({ onClose }) => {
  const [position, setPosition] = useState({ x: 160, y: 140 });
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  const draggingRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  /* ---------- Drag logic ---------- */

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!draggingRef.current) return;

      setPosition({
        x: e.clientX - offsetRef.current.x,
        y: e.clientY - offsetRef.current.y,
      });
    };

    const handleMouseUp = () => {
      draggingRef.current = false;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleMouseDown = (e) => {
    draggingRef.current = true;
    offsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  /* ---------- Load reference image ---------- */

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const storage = getStorage(
          undefined,
          "gs://cory-sat.firebasestorage.app"
        );
        const imageRef = ref(storage, "SATreference.jpeg");
        const url = await getDownloadURL(imageRef);
        setImageUrl(url);
      } catch (err) {
        console.error("Failed to load SAT reference image:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        top: position.y,
        left: position.x,
        width: 660,
        height: 400,
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0px 8px 24px rgba(0,0,0,0.25)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* HEADER */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          height: 44,
          backgroundColor: "#000",
          display: "flex",
          alignItems: "center",
          px: 1.5,
          cursor: "grab",
          userSelect: "none",
          position: "relative",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "#fff" }}
        >
          Reference
        </Typography>

        {/* CENTER: 6-dot handle */}
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            display: "grid",
            gridTemplateColumns: "repeat(3, 4px)",
            gridTemplateRows: "repeat(2, 4px)",
            gap: "4px",
            pointerEvents: "none",
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                backgroundColor: "#fff",
                opacity: 0.9,
              }}
            />
          ))}
        </Box>

        {/* RIGHT: Close */}
        <Box sx={{ marginLeft: "auto" }}>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" sx={{ color: "#fff" }} />
          </IconButton>
        </Box>
      </Box>

      {/* BODY */}
      <Box
        sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingBottom: "10px",
        }}
        >
        {loading ? (
            <CircularProgress size={28} />
        ) : imageUrl ? (
            <img
            src={imageUrl}
            alt="SAT Reference"
            style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                userSelect: "none",
                pointerEvents: "none",
            }}
            />
        ) : (
            <Typography variant="body2" color="text.secondary">
            Reference unavailable
            </Typography>
        )}
        </Box>

    </Box>
  );
};

export default ReferenceWindow;
